import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Container, Row } from 'reactstrap';
import TableBuilder from '../../../../../shared/components/table/TableBuilder';
import { getExportPrefix } from '../../../../../shared/components/table/functions';
import CardPanel from '../../../../../shared/components/widgets/CardPanel';
import { apiFetch, UnauthorizedError } from '../../../../../utils/http';

const PRODUCT_SETS_PATH = '/meta/product-sets/list';

// Meta Product Sets panel for the customer-facing Meta Catalog page. Server-side
// AG Grid datasource with cursor-based pagination (same pattern as MetaCatalogMgr).
function MetaProductSetsMgr() {
  const selectedSeller = useSelector(store => store.sellerSelect);
  const [agGrid, setAgGrid] = useState(null);

  // Cursor cache for Meta's pagination (offset -> cursor mapping)
  const cursorCacheRef = useRef({ 0: null });

  const pageSize = 100;

  const exportPrefix = getExportPrefix('meta-product-sets', selectedSeller, true);

  const columnDefs = [
    { field: 'name', headerName: 'Name' },
    { field: 'id', headerName: 'Product Set ID' },
    { field: 'product_count', headerName: 'Product Count' },
    { field: 'retailer_id', headerName: 'Retailer ID' },
    {
      field: 'live_metadata.external_url',
      headerName: 'External URL',
      cellRenderer: (params) => {
        const url = params.data?.live_metadata?.external_url;
        if (url) {
          return (
            <a href={url} target="_blank" rel="noopener noreferrer">
              {url}
            </a>
          );
        }
        return '';
      },
    },
    { field: 'live_metadata.integrity_review_status', headerName: 'Integrity Review Status' },
    { field: 'filter', headerName: 'Filter', hide: true },
  ];

  const defaultColDef = {
    sortable: true,
    filter: false,
    resizable: true,
  };

  const fetchProductSetsPage = (cursor, accountId) => apiFetch(PRODUCT_SETS_PATH, {
    method: 'POST',
    body: {
      accountId,
      data: {
        fields: ['id', 'name', 'product_count', 'retailer_id', 'filter', 'live_metadata{external_url,integrity_review_status}'],
        limit: pageSize,
        ...(cursor ? { after: cursor } : {}),
      },
    },
  });

  const datasource = {
    getRows: async (params) => {
      const { request, success, fail } = params;
      const { startRow } = request;
      const accountId = selectedSeller.parentAccountId > 0 ? selectedSeller.parentAccountId : selectedSeller.value;
      const offset = startRow || 0;

      try {
        const cursor = cursorCacheRef.current[offset];

        // Only forward navigation is supported (cursor is cached from previous page)
        if (offset > 0 && cursor === undefined) {
          console.warn('Meta: Missing cursor for offset', offset);
          fail();
          return;
        }

        const data = await fetchProductSetsPage(cursor, accountId);
        const rows = data?.data || [];

        // A short page means the end; Meta always returns cursors even on the last page.
        const hasNextPage = rows.length === pageSize && !!data?.paging?.cursors?.after;

        if (hasNextPage) {
          cursorCacheRef.current[offset + pageSize] = data.paging.cursors.after;
        }

        // No total is returned; add a sentinel row only while another page exists.
        const rowCount = hasNextPage ? offset + rows.length + 1 : offset + rows.length;

        success({ rowData: rows, rowCount });
      } catch (error) {
        if (!(error instanceof UnauthorizedError)) {
          console.error('Meta product sets fetch error:', error);
        }
        fail();
      }
    },
  };

  const parentAccess = (agGridPointer) => {
    setAgGrid(agGridPointer);
  };

  useEffect(() => {
    if (agGrid) {
      cursorCacheRef.current = { 0: null };
      agGrid.refreshData();
    }
  }, [selectedSeller.value, agGrid]);

  return (
    <Container className="todo-app">
      <Row>
        <CardPanel
          title="Meta Product Sets"
          subhead={new Date().toISOString()}
          autorenewButton={false}
          closeButton={false}
        >
          <TableBuilder
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            apiEndPoint={PRODUCT_SETS_PATH}
            showDatePicker={false}
            showFooter={false}
            rowHeight={40}
            pagination
            rowModelType="serverSide"
            datasource={datasource}
            paginationPageSize={pageSize}
            cacheBlockSize={pageSize}
            serverSideInfiniteScroll={false}
            parentControlledLoad
            editSelectedRowsButton={false}
            showImportButton={false}
            parentAccess={parentAccess}
            exportPrefix={exportPrefix}
          />
        </CardPanel>
      </Row>
    </Container>
  );
}

export default MetaProductSetsMgr;
